import { saveAs } from '@progress/kendo-file-saver';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from 'react';
import {
  Outlet, useLocation, useParams, useSearchParams,
} from 'react-router-dom';
import {
  IPatientDemographics,
  Group,
  buildReportFileName,
  REPORT_NO_COPY_METADATA_KEYS,
} from '@zero-dash/types';
import { highRiskCohorts } from '@/constants/sample';
import { LoadingPage } from '@/pages/Loading/Loading';
import getPrimaryBiosample from '@/utils/functions/biosamples/getPrimaryBiosample';
import { FileType } from '../../types/FileTracker/FileTracker.types';
import { IApproval, ICreateApproval, Label } from '../../types/Reports/Approvals.types';
import {
  GermlineReportAttachmentOptions,
  IReport,
  IRequiredGroup,
  IUpdateReportMetadataKey,
  ReportGenerationType, ReportMetadata,
  ReportType,
} from '@/types/Reports/Reports.types';
import { useAnalysisSet } from '../AnalysisSetContext';
import { useGroupList } from '@/hooks/useGroupList';
import { useUser } from '../UserContext';
import { useZeroDashSdk } from '../ZeroDashSdkContext';
import { usePatient } from '../PatientContext';
import { getAgeFromDob } from '@/utils/functions/getAgeFromDob';

interface IProps {
  initialReportType?: ReportType;
}

interface ICurrentReport {
  patientId: string;
  analysisSetId: string;
  demographics?: IPatientDemographics | null;
  isAssignedClinician: boolean;
  isAssignedCurator: boolean;
  isReadOnly: boolean;
  pendingReport: IReport | null;
  selectedReport: IReport | null | undefined;
  setSelectedReport: Dispatch<SetStateAction<IReport | null | undefined>>;
  reportMetadata?: ReportMetadata;
  reportType: ReportType;
  setReportType: (type: ReportType) => void;
  isGeneratingReport: ReportGenerationType | null;
  setIsGeneratingReport: Dispatch<SetStateAction<ReportGenerationType | null>>;
  generateRedactedReport?: IReport;
  setGenerateRedactedReport: Dispatch<SetStateAction<IReport | undefined>>;
  approvals: IApproval[];
  isApproving: 'Finalise' | IApproval | null;
  setIsApproving: Dispatch<SetStateAction<'Finalise' | IApproval | null>>;
  approve: (approvalId: string, sendNotifications: boolean) => Promise<void>;
  setApprovals: Dispatch<SetStateAction<IApproval[]>>;
  prevGermlineReport: IReport | null;
  getRequiredApprovals: (previousApprovals: IApproval[], germline?: boolean) => ICreateApproval[],
  germlineFindings: boolean;
  setGermlineFindings: Dispatch<SetStateAction<boolean>>;
  submitForApproval: () => Promise<void>;
  cancelReport: () => Promise<void>;
  updateMetadata: (metadata: ReportMetadata) => Promise<boolean>;
  uploadReport: (fileBlob: Blob, fileType: FileType) => Promise<boolean>;
  replaceReport: (
    reportId: string,
    fileBlob: Blob,
    fileType: FileType,
    comment: string,
  ) => Promise<boolean>;
  submitAmendedForApproval: (sourceReport: IReport) => Promise<void>;
  getReportFileName: (report: IReport, redacted?: boolean, fileType?: FileType) => Promise<string>;
  downloadReport: (report: IReport) => Promise<void>;
  /** Versions that have left for their recipients — sent, done or failed. */
  sentReports: IReport[];
  /** Re-reads the sent reports so their statuses reflect the server. */
  getSentReports: () => Promise<void>;
  /** Re-reads the report still in progress — pending, approved or ready to send. */
  getInProgressReport: () => Promise<void>;
  setPendingReport: Dispatch<SetStateAction<IReport | null>>;
  updateLocalReport: (reportId: string, updates: Partial<IReport>) => void;
  /** Puts the finalised in-progress report back into editing. */
  reopenReport: () => Promise<void>;
  markReportReadyToSend: (report: IReport) => Promise<void>;
  cancelReportSending: (report: IReport) => Promise<void>;
}

export const CurrentReportContext = createContext<ICurrentReport | undefined>(undefined);
CurrentReportContext.displayName = 'CurrentReportContext';

export const useReport = (): ICurrentReport => {
  const context = useContext(CurrentReportContext);

  if (!context) throw new Error('Report context is not available at this scope');

  return context;
};

export function CurrentReportProvider({
  initialReportType = 'MOLECULAR_REPORT',
}: IProps): JSX.Element {
  const zeroDashSdk = useZeroDashSdk();
  const { patientId, analysisSetId } = useParams();
  const [search, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const isReportsRoute = pathname.includes('/reports');
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useUser();
  const {
    groups,
    getGroupById,
  } = useGroupList();
  const { isReadOnly: isPatientReadOnly } = usePatient();
  const { analysisSet, demographics, isReadOnly: isAnalysisSetIsReadOnly } = useAnalysisSet();

  const isReadOnly = useMemo(() => (
    isAnalysisSetIsReadOnly || isPatientReadOnly
  ), [isPatientReadOnly, isAnalysisSetIsReadOnly]);

  const [pendingReport, setPendingReport] = useState<IReport | null>(null);
  const [selectedReport, setSelectedReport] = useState<IReport | null>();
  const [reportType, setReportType] = useState<ReportType>((search.get('reportType') as ReportType) ?? initialReportType);

  /** Active analysis/type used to guard async report responses; null after cleanup. */
  const reportScope = useRef<{ analysisSetId?: string; reportType: ReportType }>(null);

  /** Refresh the scope on navigation and invalidate it on cleanup, including unmount. */
  useEffect(() => {
    reportScope.current = { analysisSetId, reportType };
    return () => { reportScope.current = null; };
  }, [analysisSetId, reportType]);

  const [approvals, setApprovals] = useState<IApproval[]>([]);
  const [sentReports, setSentReports] = useState<IReport[]>([]);
  const [isApproving, setIsApproving] = useState<'Finalise' | IApproval | null>(null);
  const [prevGermlineReport, setPrevGermlineReport] = useState<IReport | null>(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState<ReportGenerationType | null>(null);
  const [germlineFindings, setGermlineFindings] = useState<boolean>(false);
  const [generateRedactedReport, setGenerateRedactedReport] = useState<IReport>();
  const prevAnalysisSetIdRef = useRef(analysisSetId);

  const defaultMetadata = useMemo((): ReportMetadata => {
    if (reportType === 'MOLECULAR_REPORT') {
      return {
        'molecular.hidePanel': `${highRiskCohorts.some((c) => c === analysisSet.cohort)}`,
      };
    }
    if (reportType === 'GERMLINE_REPORT') {
      const attachments: GermlineReportAttachmentOptions[] = [];
      const fullConsent = Boolean(
        demographics?.germlineConsent
        || (
          demographics?.category1Consent
          && demographics?.category2Consent
        ),
      );

      if (fullConsent && germlineFindings) {
        attachments.push('Genetics contact list');
      }

      if (!germlineFindings && demographics) {
        if (fullConsent && getAgeFromDob(demographics, true).age < 18) {
          attachments.push('No findings factsheet');
        } else if (
          demographics?.category2Consent === false
          || demographics.germlineConsent === false
        ) {
          attachments.push('No attachment');
        }
      }

      return {
        'germline.attachments': JSON.stringify(attachments),
      };
    }
    return {};
  }, [
    demographics,
    germlineFindings,
    reportType,
    analysisSet.cohort,
  ]);

  const [reportMetadata, setReportMetadata] = useState<ReportMetadata>(defaultMetadata);

  const isAssignedClinician = useMemo(() => {
    if (reportType === 'MOLECULAR_REPORT') {
      return approvals.some((a) => (
        ['MolecularOncologists', 'CancerGeneticists'].includes(a.groupName ?? '')
        && a.assigneeId === currentUser?.id
      ));
    }

    if (reportType === 'GERMLINE_REPORT') {
      return approvals.some((a) => a.groupName === 'CancerGeneticists' && a.assigneeId === currentUser?.id);
    }

    if (reportType === 'PRECLINICAL_REPORT' || reportType === 'MTB_REPORT') {
      return approvals.some((a) => (
        ['MolecularOncologists', 'ClinicalFellows', 'MTBChairs'].includes(a.groupName ?? '')
        && a.assigneeId === currentUser?.id
      ));
    }

    return false;
  }, [approvals, currentUser?.id, reportType]);

  const isAssignedCurator = useMemo(() => (
    approvals.some((a) => a.groupName === 'Curators' && a.assigneeId === currentUser?.id)
    || analysisSet.primaryCuratorId === currentUser?.id
    || analysisSet.secondaryCuratorId === currentUser?.id
  ), [analysisSet.primaryCuratorId, analysisSet.secondaryCuratorId, approvals, currentUser?.id]);

  const getReportApprovals = useCallback(() => {
    if (pendingReport?.id) {
      zeroDashSdk.services.reports.getApprovals({
        reportId: pendingReport.id,
      })
        .then((resp) => {
          setApprovals(resp.map((a) => ({
            ...a,
            groupName: getGroupById(a.groupId),
          })));
        });
    } else {
      setApprovals([]);
    }
  }, [getGroupById, pendingReport?.id, zeroDashSdk.services.reports]);

  const approve = useCallback(async (
    approvalId: string,
    sendNotifications: boolean,
  ): Promise<void> => {
    await zeroDashSdk.services.reports.updateApproval(
      approvalId,
      {
        patientId,
        status: 'approved',
        sendNotifications,
      },
    );

    setApprovals((prev) => prev.map((a) => ({
      ...a,
      status: a.id === approvalId
        ? 'approved'
        : a.status,
    })));
  }, [patientId, zeroDashSdk.services.reports]);

  const getRequiredApprovals = useCallback((
    previousApprovals: IApproval[],
    germline?: boolean,
  ): ICreateApproval[] => {
    const apps: ICreateApproval[] = [];

    const requiredGroups: IRequiredGroup[] = [];
    if (reportType === 'MOLECULAR_REPORT') {
      requiredGroups.push({ group: 'Curators', label: 'Reported by' });
      requiredGroups.push({ group: 'MolecularOncologists' });
      if (germline) requiredGroups.push({ group: 'CancerGeneticists' });
    }

    if (reportType === 'MTB_REPORT' || reportType === 'PRECLINICAL_REPORT') {
      requiredGroups.push(
        { group: 'Curators', showOnReport: false, status: 'approved' },
        { group: 'MolecularOncologists' },
        { group: 'MTBChairs' },
        { group: 'ClinicalFellows', label: 'Reported by' },
      );
    }

    if (reportType === 'GERMLINE_REPORT') {
      if (germline) {
        requiredGroups.push(
          { group: 'CancerGeneticists', label: 'Reported by' },
          { group: 'CancerGeneticists', label: 'Authorised by' },
        );
      } else {
        requiredGroups.push({ group: 'Curators', label: 'Reported by' });
        requiredGroups.push({ group: 'CancerGeneticists' });
      }
    }

    const getAssignee = (
      prevApprovals: IApproval[],
      groupName: Group | undefined,
      label: Label | undefined,
    ): string | null | undefined => {
      if (typeof label === 'string') {
        return prevApprovals.find((a) => a.label === label)?.assigneeId;
      }
      if (prevApprovals && prevApprovals.length > 0) {
        return prevApprovals.find((a) => a.groupName === groupName)?.assigneeId;
      }
      return undefined;
    };

    for (const group of requiredGroups) {
      const groupId = groups.find((g) => g.name === group.group)?.id;
      apps.push({
        status: group.status || 'pending',
        groupId,
        label: group.label || null,
        assigneeId: getAssignee(previousApprovals, group.group, group.label),
        showOnReport: group.showOnReport,
      });
    }

    return apps;
  }, [reportType, groups]);

  const submitForApproval = useCallback(async (amendSource?: IReport): Promise<void> => {
    if (!analysisSetId) return;
    if (pendingReport?.status === 'pending') {
      // Cancel pending approvals and report.
      // Really shouldn't get here, but better be safe.
      await Promise.all(approvals.map((a) => (
        zeroDashSdk.services.reports.updateApproval(a.id, { status: 'cancelled' })
      )));
      await zeroDashSdk.services.reports.updateReport(pendingReport.id, { status: 'cancelled' });
    }

    let prevApprovals: IApproval[] = [];

    if (amendSource) {
      prevApprovals = await zeroDashSdk.services.reports.getApprovals({
        reportId: amendSource.id,
      }).then((resp) => resp.map((a) => ({ ...a, groupName: getGroupById(a.groupId) })));
    } else {
      const prevReports = await zeroDashSdk.services.reports.getReports({
        analysisSetIds: [analysisSetId],
        types: [reportType],
        statuses: ['finalised', 'done', 'ready_to_send', 'failed', 'sent'],
        sortColumns: ['finalised'],
        sortDirections: ['desc'],
      });

      if (prevReports[0]) {
        prevApprovals = await zeroDashSdk.services.reports.getApprovals({
          reportId: prevReports[0].id,
        }).then((resp) => resp.map((a) => ({ ...a, groupName: getGroupById(a.groupId) })));
      }
    }

    const newReportId = await zeroDashSdk.services.reports.createReport({
      analysisSetId,
      type: reportType,
      status: 'pending',
      ...(amendSource ? { amendmentOf: amendSource.id } : {}),
    });

    const activeMetadata = {
      ...(amendSource?.metadata ?? reportMetadata),
      ...Object.fromEntries(REPORT_NO_COPY_METADATA_KEYS.map((k) => [k, undefined])),
    };

    if (Object.values(activeMetadata).some((v) => v !== undefined)) {
      await zeroDashSdk.services.reports.updateReportMetadata(
        newReportId,
        Object.entries(activeMetadata)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => ({ key, value })) as IUpdateReportMetadataKey[],
      );
    }

    const requiredApprovals: ICreateApproval[] = getRequiredApprovals(
      prevApprovals,
      germlineFindings,
    );

    await zeroDashSdk.services.reports.createApprovals({
      reportId: newReportId,
      approvals: requiredApprovals,
    });

    await zeroDashSdk.curation.analysisSets.updateAnalysisSetById(
      analysisSetId,
      { reopenCase: true },
    );

    /**
     * Fetch the saved report and document ID after Prepare succeeds.
     * Treat a failed read as null so it does not fail the completed Prepare action.
     */
    const report = await zeroDashSdk.services.reports.getReportById(newReportId)
      .catch(() => null);

    /**
     * Update state only if the same analysis and report type are currently active.
     * If the read failed, warn the user and keep a pending report with the saved UUID
     * and no document ID, preventing a retry of the completed Prepare action.
     */
    if (
      reportScope.current?.analysisSetId === analysisSetId
      && reportScope.current?.reportType === reportType
    ) {
      if (!report) {
        enqueueSnackbar('Report prepared, but its ID could not be loaded. Please reload to refresh.', {
          variant: 'warning',
        });
      }
      setPendingReport(report ?? {
        id: newReportId,
        documentId: null,
        amendmentOf: amendSource?.id ?? null,
        analysisSetId,
        type: reportType,
        status: 'pending',
        pseudoStatus: null,
        approvedAt: null,
        metadata: { ...activeMetadata },
        createdBy: currentUser?.id || '',
        createdAt: dayjs().toISOString(),
      });
      setSelectedReport(null);
    }
  }, [
    getGroupById,
    approvals,
    germlineFindings,
    getRequiredApprovals,
    reportType,
    analysisSetId,
    pendingReport?.id,
    pendingReport?.status,
    reportMetadata,
    currentUser?.id,
    enqueueSnackbar,
    zeroDashSdk.services.reports,
    zeroDashSdk.curation.analysisSets,
  ]);

  const submitAmendedForApproval = useCallback(async (sourceReport: IReport): Promise<void> => {
    await submitForApproval(sourceReport);
  }, [submitForApproval]);

  const cancelReport = useCallback(async (): Promise<void> => {
    if (!pendingReport) return;
    await zeroDashSdk.services.reports.updateReport(pendingReport.id, { status: 'cancelled' });
    setPendingReport(null);
    setSelectedReport(null);
    setApprovals([]);
  }, [pendingReport, zeroDashSdk.services.reports]);

  const updateMetadata = useCallback(async (metadata: ReportMetadata): Promise<boolean> => {
    if (!pendingReport?.id) {
      setReportMetadata(metadata);
      return true;
    }

    try {
      await zeroDashSdk.services.reports.updateReportMetadata(
        pendingReport.id,
        Object.entries(metadata)
          .filter(([, value]) => value !== undefined)
          .map(([key, value]) => ({ key, value })) as IUpdateReportMetadataKey[],
      );
      setReportMetadata(metadata);
      setPendingReport({
        ...pendingReport,
        metadata,
      });
      return true;
    } catch {
      enqueueSnackbar('Could not update report, please try again', { variant: 'error' });
    }
    return false;
  }, [enqueueSnackbar, pendingReport, zeroDashSdk.services.reports]);

  /**
   * Patches a report already held in local state. The target may be either a sent
   * version or the in-progress report, so both are updated — an unsent finalised
   * report lives in `pendingReport` and must not drift from what the server holds.
   */
  const updateLocalReport = useCallback((
    reportId: string,
    updates: Partial<IReport>,
  ): void => {
    setSentReports((prev) => prev.map((r) => (r.id === reportId ? { ...r, ...updates } : r)));
    setPendingReport((prev) => (prev?.id === reportId ? { ...prev, ...updates } : prev));
  }, []);

  const uploadReport = useCallback(async (
    fileBlob: Blob,
    fileType: FileType,
    replaceOptions?: { reportId: string; comment?: string },
  ): Promise<boolean> => {
    // A replace targets an already-approved report by id, so it must NOT require a
    // pending (in-progress) report — in practice the two never coexist, which is why
    // replacing used to silently no-op. Only the finalise path below needs one.
    const targetId = replaceOptions?.reportId ?? pendingReport?.id;
    if (!targetId) {
      enqueueSnackbar('No report available to upload to.', { variant: 'error' });
      return false;
    }

    const fileName = `${analysisSetId}-${reportType}-${dayjs().toISOString()}.${fileType}`;
    const key = `report/${analysisSetId}/${reportType}/${fileName}`;
    const fileId = await zeroDashSdk.filetracker.uploadFile(fileBlob, {
      analysisSetId,
      fileName,
      key,
      fileType,
      isHidden: true,
    });

    if (replaceOptions) {
      await zeroDashSdk.services.reports.updateReport(targetId, {
        fileId,
        ...(replaceOptions.comment ? { replaceComment: replaceOptions.comment } : {}),
      });
      updateLocalReport(targetId, { fileId });
    } else {
      if (!pendingReport) {
        enqueueSnackbar('No pending report to finalise.', { variant: 'error' });
        return false;
      }
      await zeroDashSdk.services.reports.updateReport(targetId, { fileId, status: 'finalised' });
      // Finalising does not end the report's life in this view: it can still be
      // re-opened for edits or held back from the send queue, so it stays the
      // in-progress report until it has actually gone out to its recipients.
      setPendingReport({
        ...pendingReport,
        status: 'finalised',
        fileId,
        approvedAt: dayjs().toISOString(),
      });
      setSelectedReport(null);
    }

    return true;
  }, [
    reportType,
    analysisSetId,
    pendingReport,
    enqueueSnackbar,
    updateLocalReport,
    zeroDashSdk.filetracker,
    zeroDashSdk.services.reports,
  ]);

  const getReportFileName = useCallback(async (
    report: IReport,
    redacted = false,
    fileType: FileType = 'pdf',
  ): Promise<string> => {
    if (!analysisSetId || !patientId) return '';
    const aset = await zeroDashSdk.curation.analysisSets.getAnalysisSetById(
      analysisSetId,
    );
    const primaryBiosample = getPrimaryBiosample(aset?.biosamples ?? []);

    return buildReportFileName({
      reportType: report.type,
      patientId,
      biomaterialId: primaryBiosample?.biomaterialId,
      firstName: demographics?.firstName,
      lastName: demographics?.lastName,
      approvedAt: report.approvedAt,
      fileType,
      redacted,
      isAmended: Boolean(report.amendmentOf),
    });
  }, [
    patientId,
    analysisSetId,
    demographics,
    zeroDashSdk.curation.analysisSets,
  ]);

  const downloadReport = useCallback(async (report: IReport): Promise<void> => {
    const fileName = await getReportFileName(report);
    if (fileName && report.fileId) {
      const file = await zeroDashSdk.filetracker.getFile(report.fileId);
      const fileBlob = await zeroDashSdk.filetracker.downloadFile(file?.fileId);
      saveAs(fileBlob, fileName);
    }
  }, [zeroDashSdk.filetracker, getReportFileName]);

  const replaceReport = useCallback(
    async (
      reportId: string,
      fileBlob: Blob,
      fileType: FileType,
      comment: string,
    ): Promise<boolean> => uploadReport(fileBlob, fileType, { reportId, comment }),
    [uploadReport],
  );

  useEffect(() => {
    if (pendingReport) {
      getReportApprovals();
      setReportMetadata(pendingReport.metadata || defaultMetadata);
    } else {
      setApprovals([]);
      setReportMetadata(defaultMetadata);
    }
  }, [pendingReport, getReportApprovals, defaultMetadata]);

  useEffect(() => {
    if (selectedReport === null && pendingReport?.type === reportType) {
      setSelectedReport(pendingReport);
    }
  }, [pendingReport, selectedReport, reportType]);

  // check if there was a previous germline report for this patient
  useEffect(() => {
    zeroDashSdk.curation.analysisSets.getAnalysisSetPatients({ patientId })
      .then((sets) => (
        sets.length > 0
          ? zeroDashSdk.services.reports.getReports({
            analysisSetIds: sets.flatMap((s) => s.analysisSets.map((a) => a.analysisSetId)),
            statuses: ['done', 'sent'],
            types: ['GERMLINE_REPORT'],
          })
          : Promise.resolve([])
      ))
      .then((reports) => setPrevGermlineReport(reports[0]));
  }, [patientId, zeroDashSdk.curation.analysisSets, zeroDashSdk.services.reports]);

  /**
   * Re-reads the report still in progress for this type. A report counts as in
   * progress until it has actually left for its recipients: `finalised` and
   * `ready_to_send` reports can still be re-opened for edits or pulled back out of
   * the send queue, so they belong here alongside `pending`. Consumers that mean
   * "the report is still being edited" must check `pendingReport?.status ===
   * 'pending'` rather than mere existence.
   *
   * Ignores responses from a previous analysis/type scope. Effect cleanup aborts
   * the optional signal to also ignore stale reads after selection changes;
   * this prevents state updates without cancelling the HTTP request.
   */
  const getInProgressReport = useCallback(async (signal?: AbortSignal): Promise<void> => {
    if (!analysisSetId) return;
    const scope = reportScope.current;
    const resp = await zeroDashSdk.services.reports.getReports({
      analysisSetIds: [analysisSetId],
      types: [reportType],
      statuses: ['pending', 'finalised', 'ready_to_send', 'failed'],
      sortColumns: ['drafted'],
      sortDirections: ['desc'],
    });
    if (!signal?.aborted && reportScope.current === scope) {
      setPendingReport(resp[0] || null);
    }
  }, [analysisSetId, reportType, zeroDashSdk.services.reports]);

  // Reset report state when switching events (layout effect runs before URL sync effects)
  useLayoutEffect(() => {
    if (!analysisSetId) return;
    const urlType = search.get('reportType') as ReportType | null;
    setSelectedReport(null);
    setPendingReport(null);
    setReportType(urlType ?? initialReportType);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only on event change, not each URL tick
  }, [analysisSetId]);

  // URL ↔ state for report params — only on /reports (provider also wraps /curation)
  const urlReportId = search.get('reportId');
  const urlReportType = search.get('reportType');

  const selectedReportForEvent = selectedReport?.analysisSetId === analysisSetId
    ? selectedReport
    : undefined;

  useEffect(() => {
    const controller = new AbortController();
    if (selectedReport !== undefined) {
      getInProgressReport(controller.signal);
    }
    // Ignore late reads after selection changes or unmount without cancelling the HTTP request.
    return () => controller.abort();
  }, [getInProgressReport, selectedReport]);

  /**
   * Re-reads the versions that have left for their recipients. Call after anything
   * that moves a report along the outbound pipeline: the status drives what the
   * version block reports, and the server may have advanced it further than the
   * caller knows (the send queue runs on a cron).
   */
  const getSentReports = useCallback(async (): Promise<void> => {
    if (!analysisSetId) {
      setSentReports([]);
      return;
    }
    const resp = await zeroDashSdk.services.reports.getReports({
      analysisSetIds: [analysisSetId],
      types: [reportType],
      includeApprovals: true,
      statuses: ['sent', 'done', 'failed'],
    });
    setSentReports(resp);
    // The cron may have picked the in-progress report up since it was last read.
    // Once it has been sent it belongs to the list above, not to this view.
    setPendingReport((prev) => (prev && resp.some((r) => r.id === prev.id) ? null : prev));
  }, [reportType, analysisSetId, zeroDashSdk.services.reports]);

  useEffect(() => {
    getSentReports();
  }, [getSentReports]);

  /**
   * Puts a finalised report back into editing. The approvals are deliberately left
   * as they are: re-opening is for correcting the report, not for redoing its sign
   * off, so it can be finalised again without another approval round.
   */
  const reopenReport = useCallback(async (): Promise<void> => {
    // Only a finalised report that has not gone out yet can be re-opened.
    if (pendingReport?.status !== 'finalised' && pendingReport?.status !== 'ready_to_send') {
      return;
    }
    await zeroDashSdk.services.reports.updateReport(pendingReport.id, { status: 'pending' });
    setPendingReport({ ...pendingReport, status: 'pending' });
    setSelectedReport(null);
  }, [pendingReport, zeroDashSdk.services.reports]);

  const markReportReadyToSend = useCallback(async (report: IReport): Promise<void> => {
    // Guard on the status this view holds: the send queue runs on a cron, so the
    // report may have moved on since the menu or button was rendered.
    if (report.status !== 'finalised') {
      await getSentReports();
      return;
    }
    await zeroDashSdk.services.reports.updateReport(report.id, { status: 'ready_to_send' });
    updateLocalReport(report.id, { status: 'ready_to_send' });
    // Reconcile with the server, which is the authority on the status.
    await getSentReports();
  }, [getSentReports, updateLocalReport, zeroDashSdk.services.reports]);

  const cancelReportSending = useCallback(async (report: IReport): Promise<void> => {
    if (report.status !== 'ready_to_send') {
      await getSentReports();
      return;
    }
    await zeroDashSdk.services.reports.updateReport(report.id, { status: 'finalised' });
    updateLocalReport(report.id, { status: 'finalised' });
    await getSentReports();
  }, [getSentReports, updateLocalReport, zeroDashSdk.services.reports]);

  useEffect(() => {
    if (!isReportsRoute) return;

    if (selectedReport && selectedReport.analysisSetId !== analysisSetId) {
      setSelectedReport(null);
    }

    const reportId = search.get('reportId');
    const inputReportType = search.get('reportType') as ReportType | null;

    if (inputReportType && inputReportType !== reportType) {
      setReportType(inputReportType);
    }

    if (reportId) {
      if (
        selectedReportForEvent?.id === reportId
        && selectedReportForEvent.status === 'finalised'
      ) return;
      zeroDashSdk.services.reports.getReportById(reportId)
        .then((report) => {
          if (report.analysisSetId !== analysisSetId) {
            setSelectedReport(null);
            return;
          }
          setSelectedReport(report);
        })
        .catch(() => setSelectedReport(null));
      return;
    }

    if (selectedReportForEvent?.status === 'finalised') {
      setSelectedReport(null);
      return;
    }

    if (selectedReport === undefined) {
      setSelectedReport(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- URL-only; not selection
  }, [search, isReportsRoute, zeroDashSdk.services.reports]);

  useEffect(() => {
    if (!isReportsRoute && selectedReport === undefined) {
      setSelectedReport(null);
    }
  }, [isReportsRoute, selectedReport]);

  useEffect(() => {
    if (!isReportsRoute) {
      if (!urlReportId && !urlReportType) return;
      const next = new URLSearchParams(search);
      next.delete('reportType');
      next.delete('reportId');
      setSearchParams(next, { replace: true });
      return;
    }

    // Navigation already set the URL on event switch; stale reportType must not overwrite it
    const eventJustSwitched = prevAnalysisSetIdRef.current !== analysisSetId;
    if (eventJustSwitched && urlReportType && urlReportType !== reportType) {
      return;
    }

    // Let URL→state catch up after cross-page navigation; in-app clicks update URL in handlers
    if (!selectedReportForEvent && urlReportType && urlReportType !== reportType) {
      return;
    }

    const type = selectedReportForEvent?.status === 'finalised'
      ? selectedReportForEvent.type
      : reportType;
    const id = selectedReportForEvent?.status === 'finalised'
      ? selectedReportForEvent.id
      : undefined;

    if (urlReportType === type && (id ? urlReportId === id : !urlReportId)) return;

    const next = new URLSearchParams(search);
    next.set('reportType', type);
    if (id) next.set('reportId', id);
    else next.delete('reportId');
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- omit `search` to avoid URL/state loop
  }, [selectedReport, reportType, urlReportId, urlReportType, isReportsRoute, setSearchParams]);

  useEffect(() => {
    prevAnalysisSetIdRef.current = analysisSetId;
  }, [analysisSetId]);

  const value = useMemo(() => {
    const sortApprovals = (approval: IApproval): number => {
      let order: number;
      switch (approval.groupName) {
        case 'Curators':
          order = 1;
          break;
        case 'ClinicalFellows':
          order = 2;
          break;
        case 'CancerGeneticists':
          order = 3;
          break;
        case 'MolecularOncologists':
          order = 4;
          break;
        case 'MTBChairs':
          order = 5;
          break;
        default:
          order = 6;
          break;
      }
      // If two comparing items have the same role name, sorting is decided by label
      // An item with 'Reported by' label should be displayed first
      if (approval.label === 'Reported by') {
        order -= 0.5;
      }
      return order;
    };
    return {
      patientId: analysisSet.patientId,
      analysisSetId: analysisSet.analysisSetId,
      demographics,
      pendingReport,
      reportMetadata,
      isAssignedClinician,
      isAssignedCurator,
      isReadOnly,
      selectedReport,
      setSelectedReport,
      reportType,
      setReportType,
      isGeneratingReport,
      setIsGeneratingReport,
      approvals: approvals.sort((a, b) => sortApprovals(a) - sortApprovals(b)),
      approve,
      setApprovals,
      isApproving,
      setIsApproving,
      prevGermlineReport,
      getRequiredApprovals,
      germlineFindings,
      setGermlineFindings,
      submitForApproval,
      cancelReport,
      updateMetadata,
      uploadReport,
      replaceReport,
      submitAmendedForApproval,
      downloadReport,
      getReportFileName,
      generateRedactedReport,
      setGenerateRedactedReport,
      sentReports,
      getSentReports,
      getInProgressReport,
      setPendingReport,
      updateLocalReport,
      reopenReport,
      markReportReadyToSend,
      cancelReportSending,
    };
  }, [
    analysisSet.analysisSetId,
    analysisSet.patientId,
    approvals,
    approve,
    demographics,
    downloadReport,
    generateRedactedReport,
    germlineFindings,
    getReportFileName,
    getRequiredApprovals,
    isApproving,
    isAssignedClinician,
    isAssignedCurator,
    isGeneratingReport,
    isReadOnly,
    pendingReport,
    prevGermlineReport,
    replaceReport,
    reportMetadata,
    reportType,
    selectedReport,
    submitAmendedForApproval,
    submitForApproval,
    cancelReport,
    updateLocalReport,
    updateMetadata,
    uploadReport,
    sentReports,
    getSentReports,
    getInProgressReport,
    reopenReport,
    markReportReadyToSend,
    cancelReportSending]);

  return isReportsRoute && selectedReport === undefined
    ? <LoadingPage />
    : (
      <CurrentReportContext.Provider value={value}>
        <Outlet />
      </CurrentReportContext.Provider>
    );
}
